import { Can } from '@/components/can/Can';
import { DataTableColumnHeader } from '@/components/dataTable/ColumnHeader';
import { getSerialNumber } from '@/components/dataTable/data-table.utils';
import { PERMS } from '@/features/auth/permissions/permissionKeys';
import { ModalSize, ModalVariant } from '@/features/configuration-management/location/table/Column';
import { TrashIcon } from '@/icons';
import { SingleUserCog } from '@/icons/single-user-cog';
import { formatDate } from '@/lib/dateFormatter';
import { StandardizedApiError } from '@/services/client/types';
import { DeleteMainPageStorePayload } from '@/services/mainPage/storeList/storeList.mutations';
import { StoreList, StoreListStatus } from '@/services/mainPage/storeList/storeList.types';
import { UseMutateAsyncFunction } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { ColumnDef } from '@tanstack/react-table';

export function getColumns(
  t: (key: string, options?: Record<string, string>) => string,
  groupId: string,
  openConfirmation: (data: {
    title: string;
    description?: string | undefined;
    cancelLabel: string;
    actionLabel: string;
    content?: React.ReactNode;
    size?: ModalSize;
    variant?: ModalVariant;
    onAction?: () => void | Promise<void>;
    onCancel?: () => void | Promise<void>;
    isSubmitting?: 'submit' | 'cancel' | null;
  }) => void,
  mutateAsync: UseMutateAsyncFunction<
    {
      message: string;
    },
    StandardizedApiError,
    DeleteMainPageStorePayload,
    unknown
  >,
  locationId: string,
): ColumnDef<StoreList>[] {
  return [
    {
      accessorKey: 'sn',
      header: ({ column }) => (
        <DataTableColumnHeader className="whitespace-nowrap" column={column} title={t('common.page.tableColumn.sn')} />
      ),
      cell: ({ row, table }) => <div className="font-medium">{getSerialNumber(table, row.index)}</div>,
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: 'storeNameEng',
      header: ({ column }) => (
        <DataTableColumnHeader
          className="whitespace-nowrap"
          column={column}
          title={t('common.page.tableColumn.storeInfo')}
        />
      ),
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <div className="size-8 rounded-full overflow-hidden shrink-0">
            <img
              src={row.original.storeLogo.path}
              alt={row.original.storeLogo.alt}
              className="h-full w-full object-cover"
            />
          </div>
          <div>
            {row.original.clubCategory === 'premium' ? (
              <Link
                to="/store-information/premium-stores"
                search={{ q: row.original.storeNameEng }}
                className="font-medium text-xs text-link underline"
              >
                {row.original.storeNameEng}
              </Link>
            ) : (
              <Link
                to="/store-information/basic-stores"
                search={{ q: row.original.storeNameEng }}
                className="font-medium text-xs text-link underline"
              >
                {row.original.storeNameEng}
              </Link>
            )}
            <div className="text-[12px] text-foreground-subtle font-medium whitespace-nowrap">
              {row.original.storeNameJp}
            </div>
          </div>
        </div>
      ),
      enableSorting: true,
      enableHiding: false,
    },
    {
      accessorKey: 'displayPageLabel',
      header: ({ column }) => (
        <DataTableColumnHeader
          className="whitespace-nowrap"
          column={column}
          title={t('common.page.tableColumn.displayPage')}
        />
      ),
      cell: ({ row }) => <div className="font-medium text-caption">{row.original.displayPageLabel}</div>,
      enableSorting: true,
      enableHiding: false,
    },
    {
      accessorKey: 'displayOrder',
      header: ({ column }) => (
        <DataTableColumnHeader
          className="whitespace-nowrap"
          column={column}
          title={t('common.page.tableColumn.displayOrder')}
        />
      ),
      cell: ({ row }) => (
        <div className="font-medium text-caption">{row.original.displayOrder ? row.original.displayOrder : '-'}</div>
      ),
      enableSorting: true,
      enableHiding: false,
    },
    {
      accessorKey: 'requestedDate',
      header: ({ column }) => (
        <DataTableColumnHeader
          className="whitespace-nowrap"
          column={column}
          title={t('common.page.tableColumn.requestedDate')}
        />
      ),
      cell: ({ row }) => (
        <div className="font-medium text-caption">
          {row.original.requestedDate ? formatDate(Number(row.original.requestedDate)) : '-'}
        </div>
      ),
      enableSorting: true,
      enableHiding: false,
    },
    {
      accessorKey: 'status',
      header: ({ column }) => (
        <DataTableColumnHeader
          className="whitespace-nowrap"
          column={column}
          title={t('common.page.tableColumn.status')}
        />
      ),
      cell: ({ row }) => {
        const status = row.original.status;
        return (
          <div>
            {status === StoreListStatus.PENDING ? (
              <div className="text-warning text-caption font-medium">{t('common.page.table.pending')}</div>
            ) : status === StoreListStatus.APPROVED ? (
              <div className="text-success text-caption font-medium">{t('common.page.table.approved')}</div>
            ) : (
              <div className="text-destructive-text text-caption font-medium">{t('common.page.table.rejected')}</div>
            )}
          </div>
        );
      },
      enableSorting: true,
      enableHiding: false,
    },
    {
      accessorKey: 'updatedDate',
      header: ({ column }) => (
        <DataTableColumnHeader
          className="whitespace-nowrap"
          column={column}
          title={t('common.page.tableColumn.updatedDate')}
        />
      ),
      cell: ({ row }) => (
        <div className="font-medium text-caption">
          {row.original.updatedDate ? formatDate(Number(row.original.updatedDate)) : '-'}
        </div>
      ),
      enableSorting: true,
      enableHiding: false,
    },
    {
      id: 'actions',
      header: ({ column }) => (
        <DataTableColumnHeader
          className="whitespace-nowrap"
          column={column}
          title={t('common.page.tableColumn.action')}
        />
      ),
      cell: ({ row }) => (
        <div className="flex items-center gap-4">
          <Can rule={{ allOf: [PERMS.recommendation.mainPage.host.get] }}>
            <Link
              to="/recommendation-management/main-page/$store-list/$host-list"
              params={{
                'store-list': groupId,
                'host-list': row.original.clubId,
              }}
              search={{
                locationId,
                displayOrderId: row.original.displayOrder,
                recommendationId: row.original.recommendationId,
              }}
              className="cursor-pointer"
            >
              <SingleUserCog className="size-4 text-foreground-subtle" />
            </Link>
          </Can>
          <Can rule={{ allOf: [PERMS.recommendation.mainPage.store.delete] }}>
            <div className="cursor-pointer">
              <TrashIcon
                className="size-4 text-destructive-text"
                onClick={() =>
                  openConfirmation({
                    title: t('common.page.modal.confirmation.title'),
                    description: t('storeManagement.form.deleteModel.description', {
                      storeName: row.original.storeNameEng,
                    }),
                    cancelLabel: t('common.page.modal.no'),
                    actionLabel: t('common.page.modal.confirm'),
                    size: 'lg',
                    onAction: async () => {
                      await mutateAsync({ recommendationId: row.original.recommendationId });
                    },
                    onCancel: () => {},
                    variant: 'destructive',
                  })
                }
              />
            </div>
          </Can>
        </div>
      ),
    },
  ];
}
